import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner } from './Spinner';

type Message = {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
};

const AIChat: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Load chat history when component mounts
  useEffect(() => {
    if (gameId) {
      // Generate a new session ID if one doesn't exist
      const storedSessionId = localStorage.getItem(`chat_session_${gameId}`);
      if (storedSessionId) {
        setSessionId(storedSessionId);
        loadChatHistory(storedSessionId);
      } else {
        const newSessionId = crypto.randomUUID();
        setSessionId(newSessionId);
        localStorage.setItem(`chat_session_${gameId}`, newSessionId);
      }
    }
  }, [gameId]);

  // Scroll to bottom whenever chat history updates
  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  const loadChatHistory = async (sessionId: string) => {
    try {
      setIsLoading(true);
      const response = await fetch(`/api/games/${gameId}/ai-chat-history?sessionId=${sessionId}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          // If no history found, that's okay for new sessions
          setChatHistory([]);
          return;
        }
        throw new Error('Failed to load chat history');
      }
      
      const data = await response.json() as { messages?: Message[] };
      setChatHistory(data.messages || []);
    } catch (err) {
      console.error('Error loading chat history:', err);
      setError('Failed to load previous conversation');
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitMessage();
    }
  };

  const submitMessage = async () => {
    if (!message.trim() || isLoading) return;
    
    // Add user message to chat immediately for responsive UI
    const userMessage = { role: 'user' as const, content: message.trim() };
    setChatHistory(prev => [...prev, userMessage]);
    setMessage('');
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Optimistic UI update - show typing indicator
      setChatHistory(prev => [...prev, { role: 'assistant' as const, content: '...' }]);
      
      const response = await fetch(`/api/games/${gameId}/ai-advice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt: userMessage.content,
          sessionId: sessionId,
        }),
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to get AI response');
      }
      
      const data = await response.json() as { response?: { content: string } };
      
      // Replace typing indicator with actual response
      setChatHistory(prev => {
        const newHistory = [...prev];
        // Remove the last item (typing indicator)
        newHistory.pop();
        // Add the actual response
        return [...newHistory, { 
          role: 'assistant' as const, 
          content: data.response?.content || 'I encountered an issue processing your request.'
        }];
      });
      
    } catch (err) {
      console.error('Error submitting message:', err);
      setChatHistory(prev => {
        const newHistory = [...prev];
        // Remove the typing indicator
        newHistory.pop();
        return newHistory;
      });
      setError('Failed to get a response. Please try again.');
    } finally {
      setIsLoading(false);
      // Focus the input field after submission
      inputRef.current?.focus();
    }
  };

  const formatTimestamp = (timestamp: string) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const startNewConversation = () => {
    if (window.confirm('Start a new conversation? This will clear the current chat history.')) {
      const newSessionId = crypto.randomUUID();
      setSessionId(newSessionId);
      localStorage.setItem(`chat_session_${gameId}`, newSessionId);
      setChatHistory([]);
    }
  };

  return (
    <div className="flex flex-col h-[500px] bg-white rounded-lg shadow-md dark:bg-gray-800">
      <div className="p-4 bg-primary dark:bg-primary-dark text-white rounded-t-lg flex justify-between items-center">
        <h2 className="text-lg font-semibold">Game Assistant</h2>
        <button 
          onClick={startNewConversation}
          className="text-xs bg-primary-dark px-2 py-1 rounded hover:bg-primary-darker transition dark:bg-primary dark:hover:bg-primary-light"
        >
          New Chat
        </button>
      </div>
      
      <div className="flex-1 overflow-auto p-4">
        {chatHistory.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
            <p>Ask me anything about this game!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {chatHistory.map((msg, index) => (
              <div 
                key={index} 
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div 
                  className={`max-w-[80%] p-3 rounded-lg ${
                    msg.role === 'user' 
                      ? 'bg-primary-light/20 text-gray-800 dark:bg-primary-dark/30 dark:text-gray-100' 
                      : msg.content === '...' 
                        ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400' 
                        : 'bg-white border border-gray-200 text-gray-800 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100'
                  }`}
                >
                  {msg.content === '...' ? (
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  ) : (
                    <div>
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                      {msg.timestamp && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {formatTimestamp(msg.timestamp)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>
      
      {error && (
        <div className="px-4 py-2 bg-red-100 text-red-800 text-sm dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      )}
      
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex space-x-2">
          <textarea
            ref={inputRef}
            className="flex-1 p-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary sm:text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            placeholder="Ask about game strategy, rules, or scoring..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
            disabled={isLoading}
          />
          <button
            className="bg-primary text-white px-4 rounded-lg hover:bg-primary-dark transition-colors disabled:bg-gray-300 dark:disabled:bg-gray-600"
            onClick={submitMessage}
            disabled={isLoading || !message.trim()}
          >
            {isLoading ? <Spinner size="sm" /> : 'Send'}
          </button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          Press Enter to send, Shift+Enter for a new line
        </p>
      </div>
    </div>
  );
};

export default AIChat; 